import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Stack,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as ReactLink } from 'react-router-dom';

import CancelButton from '@/components/atoms/CancelButton';
import { useSnackbar } from '@/components/hooks/useSnackbar';
import LabeledIconButton from '@/components/molecules/LabeledIconButton';
import { useEditChatRoomGuideLoginMutation } from '@/types/graphql';

export type RoomInvitation = {
  url: string;
  guideUrl?: string;
  entryCode?: string;
  chatRoomId?: string;
  guideLoginId?: string;
  guideLoginPassword?: string;
};

export type InvitingRoomDialogProps = {
  open: boolean;
  onClose: () => void;
  invitation: RoomInvitation;
  // caller: 'chat' | 'admin';
};

const MyAccordion = styled((props: AccordionProps) => (
  <Accordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  borderRadius: 0,
  boxShadow: 'none',
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&:before': {
    display: 'none',
  },
}));

const InvitingRoomDialog = (props: InvitingRoomDialogProps) => {
  const { t } = useTranslation('chat');
  const { t: tCommon } = useTranslation('common');
  const showSnackbar = useSnackbar();

  const [isEditingGuideLogin, setIsEditingGuideLogin] = useState(false);
  const [guideLoginId, setGuideLoginId] = useState(
    props.invitation.guideLoginId ?? ''
  );
  const [guideLoginPassword, setGuideLoginPassword] = useState(
    props.invitation.guideLoginPassword ?? ''
  );
  const [editGuideLoginIdValue, setEditGuideLoginIdValue] = useState('');
  const [editGuideLoginPasswordValue, setEditGuideLoginPasswordValue] =
    useState('');

  const [editChatRoomGuideLogin, { loading: isSavingGuideLogin }] =
    useEditChatRoomGuideLoginMutation();

  // このダイアログはルーム一覧側で使い回される単一のインスタンスのため、
  // useStateの初期値（マウント時の1回きり）だけでは別ルームを開いたときに
  // 前回のルームの値が残ってしまう。開くたびに対象ルームのpropsへ明示的に
  // 同期し直す（表示中の値が別ルームのものに固定される致命的な不具合の修正）。
  useEffect(() => {
    if (!props.open) return;
    setGuideLoginId(props.invitation.guideLoginId ?? '');
    setGuideLoginPassword(props.invitation.guideLoginPassword ?? '');
    setIsEditingGuideLogin(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.open,
    props.invitation.chatRoomId,
    props.invitation.guideLoginId,
    props.invitation.guideLoginPassword,
  ]);

  const startEditingGuideLogin = () => {
    setEditGuideLoginIdValue(guideLoginId);
    setEditGuideLoginPasswordValue(guideLoginPassword);
    setIsEditingGuideLogin(true);
  };

  const saveGuideLogin = async () => {
    if (props.invitation.chatRoomId == null) return;
    const result = await editChatRoomGuideLogin({
      variables: {
        input: {
          chatRoomId: props.invitation.chatRoomId,
          guideLoginId: editGuideLoginIdValue,
          guideLoginPassword: editGuideLoginPasswordValue,
        },
      },
    });
    const data = result.data?.editChatRoomGuideLogin;
    if (data?.__typename === 'GuideLoginIdAlreadyExists') {
      showSnackbar('error', t('invitation.guideLoginIdAlreadyExists'));
      return;
    }
    if (data?.__typename === 'ChatRoom') {
      setGuideLoginId(data.guideLoginId);
      setGuideLoginPassword(data.guideLoginPassword);
      setIsEditingGuideLogin(false);
      showSnackbar('success', t('invitation.guideLoginUpdated'));
    }
  };

  return (
    <Dialog open={props.open} onClose={props.onClose} scroll="body">
      <DialogTitle>{t('invitation.title')}</DialogTitle>
      <Divider />
      <DialogContent>
        <Stack spacing={1} mb={1} mt={-1}>
          <Typography variant="h6">{t('invitation.inviteGuest')}</Typography>
          <Box pt={1}>
            <Typography variant="subtitle1">
              {t('invitation.inviteViaUrl')}
            </Typography>
          </Box>
          <Box
            display="grid"
            gridTemplateColumns="1fr auto"
            alignItems="center"
          >
            <Stack>
              <Typography variant="body1">
                {t('invitation.roomUrl')} :
              </Typography>
              <Typography
                sx={{ overflowWrap: 'anywhere', textAlign: 'left' }}
                variant="body2"
              >
                <Link to={props.invitation.url} component={ReactLink}>
                  {props.invitation.url}
                </Link>
              </Typography>
              {props.invitation.entryCode !== '' && (
                <Typography variant="body1" my={1}>
                  {tCommon('caption.entryCode')} : {props.invitation.entryCode}
                </Typography>
              )}
            </Stack>
            <LabeledIconButton
              color="primary"
              sx={{ width: 60, height: 60 }}
              label={tCommon('command.copy')}
              onClick={() => {
                navigator.clipboard.writeText(props.invitation.url);
              }}
            >
              <ContentCopyIcon />
            </LabeledIconButton>
          </Box>
        </Stack>
        <Stack spacing={1} my={3}>
          <Typography variant="subtitle1">
            {t('invitation.inviteViaQrCode')}
          </Typography>
          <Box
            display="grid"
            gridTemplateColumns="1fr auto 1fr"
            alignItems="center"
          >
            <Box></Box>
            <QRCodeCanvas
              value={props.invitation.url}
              id={'qrCodeEl'}
              size={120}
              includeMargin
            />
            <LabeledIconButton
              color="primary"
              label={tCommon('command.download')}
              sx={{ width: 60, height: 60, ml: 'auto' }}
              onClick={(ev) => {
                ev.preventDefault();
                const qrCodeUrl = (
                  document.getElementById('qrCodeEl') as HTMLCanvasElement
                )?.toDataURL('image/png');
                const aEl = document.createElement('a');
                aEl.href = qrCodeUrl;
                aEl.download = 'QR_Code.png';
                document.body.appendChild(aEl);
                aEl.click();
                document.body.removeChild(aEl);
              }}
            >
              <DownloadIcon />
            </LabeledIconButton>
          </Box>
        </Stack>
      </DialogContent>

      {props.invitation.guideUrl && (
        <MyAccordion sx={{ m: 0 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" mx={2}>
              {t('invitation.inviteGuide')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ mx: 2, mt: -1.5 }}>
              <Stack spacing={1} mb={2}>
                {isEditingGuideLogin ? (
                  <Stack spacing={1} alignItems="flex-start">
                    <TextField
                      label={t('invitation.guideLoginId')}
                      value={editGuideLoginIdValue}
                      onChange={(ev) =>
                        setEditGuideLoginIdValue(
                          ev.target.value.replace(/\D/g, '').slice(0, 10)
                        )
                      }
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label={t('invitation.guideLoginPassword')}
                      value={editGuideLoginPasswordValue}
                      onChange={(ev) =>
                        setEditGuideLoginPasswordValue(
                          ev.target.value.replace(/\D/g, '').slice(0, 6)
                        )
                      }
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                      size="small"
                      fullWidth
                    />
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={
                          isSavingGuideLogin ||
                          editGuideLoginIdValue.length !== 10 ||
                          editGuideLoginPasswordValue.length !== 6
                        }
                        onClick={() => saveGuideLogin()}
                      >
                        {tCommon('command.save')}
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        disabled={isSavingGuideLogin}
                        onClick={() => setIsEditingGuideLogin(false)}
                      >
                        {tCommon('command.cancel')}
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <Typography variant="body1">
                      {t('invitation.guideLoginId')}：{guideLoginId}
                      {'　'}
                      {t('invitation.guideLoginPassword')}：
                      {guideLoginPassword}
                    </Typography>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => startEditingGuideLogin()}
                    >
                      {tCommon('command.change')}
                    </Button>
                  </Stack>
                )}
              </Stack>
              <Stack spacing={1} mb={1}>
                <Box pt={1}>
                  <Typography variant="subtitle1">
                    {t('invitation.inviteViaUrl')}
                  </Typography>
                </Box>
                <Box
                  display="grid"
                  gridTemplateColumns="1fr auto"
                  alignItems="center"
                >
                  <Stack>
                    <Typography variant="body1">
                      {t('invitation.roomUrl') + '：'}
                    </Typography>
                    <Typography
                      sx={{ overflowWrap: 'anywhere', textAlign: 'left' }}
                      variant="body2"
                    >
                      <Link
                        to={props.invitation.guideUrl}
                        component={ReactLink}
                      >
                        {props.invitation.guideUrl}
                      </Link>
                    </Typography>
                  </Stack>
                  <LabeledIconButton
                    color="primary"
                    sx={{ width: 60, height: 60 }}
                    label={tCommon('command.copy')}
                    onClick={() => {
                      navigator.clipboard.writeText(
                        props.invitation.guideUrl ?? ''
                      );
                    }}
                  >
                    <ContentCopyIcon />
                  </LabeledIconButton>
                </Box>
              </Stack>
              <Stack spacing={1} my={3}>
                <Typography variant="subtitle1">
                  {t('invitation.inviteViaQrCode')}
                </Typography>
                <Box
                  display="grid"
                  gridTemplateColumns="1fr auto 1fr"
                  alignItems="center"
                >
                  <Box></Box>
                  <QRCodeCanvas
                    value={props.invitation.guideUrl ?? ''}
                    id={'qrCodeElGuide'}
                    size={120}
                    includeMargin
                  />
                  <LabeledIconButton
                    color="primary"
                    label={tCommon('command.download')}
                    sx={{ width: 60, height: 60, ml: 'auto' }}
                    onClick={(ev) => {
                      ev.preventDefault();
                      const qrCodeUrl = (
                        document.getElementById(
                          'qrCodeElGuide'
                        ) as HTMLCanvasElement
                      )?.toDataURL('image/png');
                      const aEl = document.createElement('a');
                      aEl.href = qrCodeUrl;
                      aEl.download = 'QR_Code.png';
                      document.body.appendChild(aEl);
                      aEl.click();
                      document.body.removeChild(aEl);
                    }}
                  >
                    <DownloadIcon />
                  </LabeledIconButton>
                </Box>
              </Stack>
            </Box>
          </AccordionDetails>
        </MyAccordion>
      )}
      <Divider />
      <DialogActions sx={{ justifyContent: 'center' }}>
        <CancelButton onClick={() => props.onClose()} />
      </DialogActions>
    </Dialog>
  );
};
export default InvitingRoomDialog;
